import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment-timezone'

import { cloneGallery } from '@/lib/utils'

import submitPlatformPost from '@/graphql/mutations/submissions/submitPlatformPost.gql'
import reschedulePlatformPost from '@/graphql/mutations/submissions/reschedulePlatformPost.gql'
import deletePlatformPost from '@/graphql/mutations/submissions/deletePlatformPost.gql'
import duplicatePlatformPost from '@/graphql/mutations/submissions/duplicatePlatformPost.gql'

import instagramPost from '@/graphql/queries/instagram/instagramPost.gql'
import allInstagramSubmissions from '@/graphql/queries/instagram/allInstagramSubmissions.gql'

export default {
  code: 'instagram',
  name: 'Instagram',
  icon: 'instagram',
  color: '#e1306c',
  accountNoun: 'Account',
  postNoun: 'Post',
  internalPostNoun: 'InstagramPost',
  accountCapabilities: [
    'Submit posts, reels and stories.',
    'View and reply to comments.',
    'View and reply to private DMs.'
  ],
  mutations: {
    submitPost: submitPlatformPost,
    submitPostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    deletePost: deletePlatformPost,
    deletePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    reschedulePost: reschedulePlatformPost,
    reschedulePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    duplicatePost: duplicatePlatformPost
  },
  queries: {
    allSubmissions: allInstagramSubmissions,
    getPost: instagramPost
  },
  posts: {
    preparePostForScheduling: (post, publishingStatus = 'READY_TO_PUBLISH') => {
      let clonedGallery = cloneDeep(post.gallery)
      let gallery = null
      if (clonedGallery) {
        gallery = {
          id: clonedGallery.id,
          galleryMediaSet: []
        }
        gallery.galleryMediaSet = clonedGallery.galleryMediaSet.map(
          (mediaSet) => {
            return {
              id: mediaSet.id,
              order: mediaSet.order,
              media: {
                name: mediaSet.media.name,
                url: mediaSet.media.url,
                externalId: mediaSet.media.externalId,
                mimeType: mediaSet.media.mimeType,
                size: mediaSet.media.size,
                source: mediaSet.media.source
              }
            }
          }
        )
      }

      let shareToFeed = post.shareToFeed
      if (post.target.toLowerCase() !== 'reel') {
        shareToFeed = false
      }

      let submissions = post.submissions.map((submission) => {
        return {
          id: submission.id,
          postAt: submission.postAt,
          // TODO: Update this when we support multiple submissions on the page
          mediaType: post.target.toUpperCase(),
          shareToFeed
        }
      })

      return {
        id: post.id,
        username: post.username,
        gallery,
        caption: post.caption,
        firstComment: post.firstComment,
        collaborators: post.collaborators || [],
        submissions,
        publishingStatus
      }
    },
    createPost(socialAccount) {
      return {
        id: null,
        username: socialAccount.username,
        target: 'feed_post',
        caption: '',
        firstComment: '',
        collaborators: [],
        publishingStatus: 'READY_TO_PUBLISH',
        gallery: {
          id: null,
          galleryMediaSet: []
        },
        submissions: [
          {
            mediaType: 'feed_post',
            postAt: moment().add(1, 'hour').startOf('hour').toDate(),
            shareToFeed: false
          }
        ]
      }
    },
    getPost(post) {
      return {
        caption: post.caption,
        text: '',
        gallery: cloneGallery(post.gallery),
        postAt: moment(post.submissions[0].postAt).toDate(),
        postType: post.submissions[0].mediaType.toLowerCase(),
        firstComment: post.firstComment
      }
    },
    syncField(post, field, value, socialAccount) {
      if (field === 'caption' || field === 'text') {
        post.caption = value
      } else if (field === 'gallery' && value) {
        post.gallery = cloneDeep(value)
      } else if (field === 'postAt') {
        post.submissions[0].postAt = value
      } else if (field === 'thread') {
        if (value.length > 0) {
          let text = value[0].text
          if (text) {
            post.caption = text
          }

          let gallery = value[0].gallery
          if (gallery) {
            post.gallery = cloneDeep(gallery)
          }
        }
      } else if (field === 'postType') {
        // Facebook uses 'post' as the mediaType for feed posts.
        const mediaTypeMap = {
          post: 'feed_post',
          carousel: 'feed_post'
        }
        value = value.toLowerCase()
        const mediaType = mediaTypeMap[value] || value
        post.target = mediaType
        post.submissions[0].mediaType = mediaType
      } else if (field === 'firstComment') {
        post.firstComment = value
      }
      return post
    },
    syncCoverImage(post, media) {
      const gallery = post.gallery
      if (gallery && gallery.galleryMediaSet.length > 0) {
        gallery.galleryMediaSet.forEach((mediaItem) => {
          if (mediaItem.media.id === media.id) {
            mediaItem.media.coverImage = media.coverImage
          }
        })
      }
      return post
    }
  },
  analytics: {
    metrics(socialAccount) {
      let metrics = [
        {
          metric: 'likes',
          label: 'Likes',
          icon: 'heart',
          aggregation: 'sum'
        },
        {
          metric: 'comments',
          label: 'Comments',
          icon: 'comment',
          aggregation: 'sum'
        }
      ]

      if (socialAccount.capabilities?.viewAnalytics) {
        metrics.push(
          {
            metric: 'impressions',
            label: 'Impressions',
            icon: 'eye',
            aggregation: 'sum',
            helpText: 'Number of times your posts were seen during this period.'
          },
          {
            metric: 'shares',
            label: 'Shares',
            icon: 'share',
            aggregation: 'sum'
          },
          {
            metric: 'profile_visits',
            label: 'Profile Visits',
            icon: 'user',
            aggregation: 'sum',
            helpText: 'The number of times your profile was visited.'
          },
          // {
          //   metric: 'total_interactions',
          //   label: 'Interactions',
          //   icon: 'handshake',
          //   aggregation: 'sum',
          //   helpText:
          //     'The number of likes, saves, comments and shares on your post minus the number of unlikes, unsaves and deleted comments.'
          // },
          {
            metric: 'reach',
            label: 'Reach',
            icon: 'globe',
            aggregation: 'sum',
            helpText:
              'The number of unique users who saw your posts during this period.'
          }
        )
      }

      metrics.push({
        metric: 'posts',
        label: 'Posts Submitted',
        icon: 'paper-plane',
        aggregation: 'sum'
      })

      return metrics
    }
  }
}
